"use client";

import React from "react";
import { Cross, Loader } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function FooterMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full">
        {[1, 2, 3, 4].map((_, i) => <div key={i} data-index={i} className="flex flex-col space-y-4">
            <div className="h-5 w-24 bg-primary-foreground/10 rounded animate-pulse" />
            <div className="space-y-2">
              <div className="h-4 w-16 bg-primary-foreground/10 rounded animate-pulse" />
              <div className="h-4 w-20 bg-primary-foreground/10 rounded animate-pulse" />
            </div>
          </div>)}
      </div>;
  }
  if (!menuItems || menuItems.length === 0) {
    return null;
  }
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-8 w-full">
      {menuItems.map((item, i) => <div key={item.id} data-index={i} className="flex flex-col space-y-4">
          <h4 className="font-serif text-lg font-medium text-primary-foreground/90">
            {item.label}
          </h4>
          {item.children && item.children.length > 0 ? <ul className="flex flex-col space-y-2.5">
              {item.children.map((child, j) => <li key={child.id} data-index={j}>
                  <Link to={child.href || "#"} className="text-sm text-primary-foreground/70 hover:text-blue-300 transition-colors duration-200">
                    {child.label}
                  </Link>
                </li>)}
            </ul> : <Link to={item.href || "#"} className="text-sm text-primary-foreground/70 hover:text-blue-300 transition-colors duration-200">
              {item.label}
            </Link>}
        </div>)}
    </div>;
}
function NewsletterForm() {
  const {
    isSubmitting,
    isSubmitted,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted) {
    return <div className="p-3 bg-white/5 border border-white/10 rounded-md backdrop-blur-sm">
        <p className="text-sm text-blue-200">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-0 flex flex-col sm:flex-row gap-2 w-full">
      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem className="flex-1">
            <FormControl>
              <Input type="email" placeholder="Email address" className="bg-white/5 border-white/10 text-primary-foreground placeholder:text-primary-foreground/50 focus-visible:ring-white/20 h-10 backdrop-blur-sm transition-all" {...field} />
            </FormControl>
            <FormMessage className="text-red-300 text-xs mt-1 absolute" />
          </FormItem>} />
      <Button type="submit" variant="ghost" disabled={isSubmitting} className="h-10 text-primary-foreground hover:bg-white/10 hover:text-primary-foreground border border-white/20 backdrop-blur-sm transition-all shrink-0 mt-6 sm:mt-0">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            Subscribing
          </> : "Subscribe"}
      </Button>
    </Form>;
}
export default function StSampsonFooter() {
  return <section id="st-sampson-footer" className="bg-background">
      <div className="bg-primary text-primary-foreground pt-16 pb-8">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-16">
            
            {}
            <div className="lg:col-span-3 flex flex-col items-start">
              <Link to="/" className="inline-block mb-6">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1590036/img/st_sampson_foundation_white_logo.png" alt="St. Sampson Senior Aid Foundation" className="h-7 w-auto object-contain" />
              </Link>
              <p className="text-sm text-primary-foreground/70 leading-relaxed max-w-xs">
                A charitable ministry dedicated to helping seniors live their final years with dignity, companionship, and faith in their own homes.
              </p>
              <div className="mt-6 space-y-1 text-sm text-primary-foreground/60">
                <p>1234 Community Lane, Anytown, State, ZIP</p>
                <p>(555) 987-6543</p>
                <p>contact@stsampsonaid.org</p>
              </div>
            </div>

            {}
            <div className="lg:col-span-6 flex justify-center">
              <MenuProvider menu_id="48" className="w-full">
                <FooterMenuConsumer />
              </MenuProvider>
            </div>

            {}
            <div className="lg:col-span-3 flex flex-col items-start">
              <h4 className="font-serif text-lg font-medium text-primary-foreground/90 mb-3">
                Stay Updated
              </h4>
              <p className="text-sm text-primary-foreground/70 mb-5 leading-relaxed">
                Join our newsletter to receive updates on our ministry, volunteer opportunities, and stories of faith.
              </p>
              <FormProvider formId="footer-newsletter" sectionName="footer" defaultValues={{
              email: ""
            }} successMessage="Thank you for subscribing to our newsletter." className="w-full">
                <NewsletterForm />
              </FormProvider>
            </div>
          </div>

          <Separator className="bg-primary-foreground/10 mb-8" />

          {}
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-sm text-primary-foreground/50">
            <p className="flex-1 text-center md:text-left order-3 md:order-1">
              © {new Date().getFullYear()} St. Sampson Senior Aid Foundation. All rights reserved.
            </p>
            
            <div className="flex-shrink-0 order-1 md:order-2">
              <Cross className="size-5 opacity-40" />
            </div>
            
            <div className="flex flex-1 justify-center md:justify-end gap-6 order-2 md:order-3">
              <Link to="#" className="hover:text-blue-300 transition-colors duration-200">
                Privacy Policy
              </Link>
              <Link to="#" className="hover:text-blue-300 transition-colors duration-200">
                Terms of Service
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>;
}